// src/components/ui/TrendSparkline.tsx
// A prior-readings trend for one numeric clinical field — the Pre-Consult
// wireframe's "Trend 8.2 → 7.6 → 7.0" beside the HbA1c box.
//
// Generic by construction, because Assessment Modules are hospital-authored:
// this takes a series and knows nothing about what it measures. Every
// template's numeric field gets the same treatment, and a template edited in
// the Admin Panel cannot produce a field this cannot draw.
//
// It deliberately does NOT say "improving" or "worsening" on its own. For an
// arbitrary numeric field, down is not necessarily better (HbA1c vs
// haemoglobin), and guessing wrong on a clinical screen is worse than saying
// nothing. Direction wording comes only from the server-supplied `color`
// band — which is the template's own declared scoring — so a module that
// never declared thresholds gets the bare numbers and an arrow, which is
// still true.
import { cn } from '@/lib/cn';

export type TrendColor = 'green' | 'yellow' | 'red' | null;

export interface TrendPoint {
  value: number;
  color?: TrendColor;
  recorded_at?: string | null;
  visit_id?: string | null;
}

const COLOR_VAR: Record<'green' | 'yellow' | 'red', string> = {
  green: 'var(--color-success-ink)',
  yellow: 'var(--color-warn-ink)',
  red: 'var(--color-danger-ink)',
};

/** green < yellow < red — used only to compare the first and last banded
 * reading, never to invent a band the template didn't declare. */
const SEVERITY: Record<'green' | 'yellow' | 'red', number> = { green: 0, yellow: 1, red: 2 };

function pointColor(c: TrendColor | undefined): string {
  return c ? COLOR_VAR[c] : 'var(--color-ink-muted)';
}

/** "improving"/"worsening" only when the template's own scoring says the band
 * moved; otherwise a plain direction, or nothing for a flat/short series. */
function directionLabel(points: TrendPoint[]): { text: string; tone: 'success' | 'danger' | 'muted' } | null {
  if (points.length < 2) return null;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (first.color && last.color) {
    const delta = SEVERITY[last.color] - SEVERITY[first.color];
    if (delta < 0) return { text: '↓ improving', tone: 'success' };
    if (delta > 0) return { text: '↑ worsening', tone: 'danger' };
  }
  if (last.value < first.value) return { text: '↓ down', tone: 'muted' };
  if (last.value > first.value) return { text: '↑ up', tone: 'muted' };
  return null;
}

/** "12 Jun" for a dated point; "Now" for the trailing point currently being
 * entered (no `recorded_at` yet — see the props doc comment below). Fixed
 * month abbreviations, not `Intl`'s `month: 'short'` — CLAUDE.md's own note
 * on newer ICU builds printing "Sept" instead of "Sep". */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function pointDateLabel(iso: string | null | undefined): string {
  if (!iso) return 'Now';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getDate()} ${MONTH_ABBR[d.getMonth()]}`;
}

export interface TrendSparklineProps {
  /** Oldest first, and the last point may be the reading currently being
   * entered (no `recorded_at`) so the trend ends where the patient is now.
   * Fewer than two points renders nothing — a single reading is not a trend,
   * and drawing one implies a history that isn't there. Recency of the prior
   * reading is the caller's to state; this shows values and direction. */
  points: TrendPoint[];
  unit?: string;
  className?: string;
}

export function TrendSparkline({ points, unit, className }: TrendSparklineProps) {
  if (points.length < 2) return null;

  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1; // a flat series draws a flat line, not a divide-by-zero
  const W = 200;
  const H = 44;
  const PAD_X = 16;
  // Asymmetric vertical padding: room above every point for its own value
  // label (mockup's on-chart "8.2 → 7.6 → 7.0"), less needed below.
  const PAD_TOP = 16;
  const PAD_BOTTOM = 4;
  const x = (i: number) => (points.length === 1 ? W / 2 : PAD_X + (i * (W - PAD_X * 2)) / (points.length - 1));
  const y = (v: number) => H - PAD_BOTTOM - ((v - min) / span) * (H - PAD_TOP - PAD_BOTTOM);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const dir = directionLabel(points);

  return (
    <div className={cn('mt-1.5', className)}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Previous readings: ${values.join(', ')}`}>
        <path d={path} fill="none" stroke="var(--color-line)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.value)} r={i === points.length - 1 ? 3.5 : 2.5} fill={pointColor(p.color)} />
            {/* Point labels — the value right on the chart, not just in a
                separate text line, per the Pre-Consult redesign's Phase 5. */}
            <text x={x(i)} y={Math.max(9, y(p.value) - 6)} fontSize="9" textAnchor="middle" fill="var(--color-ink-muted)">
              {p.value}
            </text>
          </g>
        ))}
      </svg>
      <div className="flex items-center justify-between text-[9.5px] text-ink-muted">
        {points.map((p, i) => <span key={i}>{pointDateLabel(p.recorded_at)}</span>)}
      </div>
      {(unit || dir) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
          {unit && <span className="text-[11px] tabular-nums text-ink-muted">{unit}</span>}
          {dir && (
            <span className={cn('text-[11px] font-semibold', dir.tone === 'success' ? 'text-success-ink' : dir.tone === 'danger' ? 'text-danger-ink' : 'text-ink-muted')}>
              {dir.text}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
